import { useMemo, type ReactNode } from 'react';
import { Cta, Empty, Loader, Logo, Page, Rule, ui } from '../ui';
import { COUNTRIES } from '@goodbuild/rules/countries';
import type { SiteId } from '@goodbuild/rules/sites';
import { currentSite, currentSpace } from '../router';
import { useApiMutation, type ApiError } from '../api';
import { useDocumentTitle } from '../title';

/**
 * The three states every screen in this app has.
 *
 * Loading, refused, and empty are written once here rather than per page,
 * because a dashboard that spells them differently on every screen reads as
 * three different products. The refusal states in particular are worth sharing:
 * a 403 is a sentence somebody has to act on, and it should say the same thing
 * wherever it comes from.
 */
export function Screen({
  title,
  aside,
  query,
  space,
  bleed,
  backdrop,
  children,
}: {
  title: string;
  /** One fact about the page, beside its title. `| undefined` for
   * `exactOptionalPropertyTypes`: a caller computing it from a condition
   * passes undefined rather than omitting the prop. */
  aside?: string | undefined;
  query: { isPending: boolean; error: ApiError | null };
  /**
   * Whose colours this screen is painted in, when the path does not say.
   *
   * Almost nothing passes this. `currentSpace` reads the audience off the path
   * — `/portal` is a studio's, `/board` is a creator's — and that is the right
   * answer for every screen whose URL belongs to one side of the product.
   *
   * Onboarding is the exception the prop exists for: one route that renders a
   * studio application or a creator application depending on the host, so the
   * page it is *showing* is the only thing that knows which audience is reading
   * it, and it says so rather than being guessed at from `/onboarding`.
   */
  space?: SiteId;
  bleed?: ReactNode | undefined;
  backdrop?: ReactNode | undefined;
  children: ReactNode;
}): React.ReactElement {
  /*
   * The aside is a fact about the data, so it waits for the data.
   *
   * Every caller computes it from what came back — `${games.length} in the
   * catalogue`, `${rows.length} waiting`, the studio's name — and a length read
   * off an array that is still `?? []` is zero. So the page that had not
   * finished loading said "0 in the catalogue" beside a spinner, in the same
   * type as the real answer, and then corrected itself. Holding it back for the
   * length of a request is the difference between *none* and *not yet known*.
   */
  const settled = !query.isPending && query.error === null;

  return (
    <Page site={currentSite()} space={space ?? currentSpace()} bleed={bleed} backdrop={backdrop}>
      <div className={ui.sectionHead ?? ''}>
        <h1 className="gb-headline">{title}</h1>
        {settled && aside ? <span>{aside}</span> : null}
      </div>
      <Rule />
      {query.isPending ? <Loading /> : query.error ? <Refusal error={query.error} /> : children}
    </Page>
  );
}

/**
 * What a screen says while it is still asking.
 *
 * One component rather than the word `Loading…` typed at each call site, so
 * every surface in the app waits in the same voice — and so there was one place
 * to change when waiting stopped being a line of text, which it now has.
 *
 * `Empty` is what this used to render, and that was the whole problem: the
 * dashed box is the *there is nothing here* state, so a table still loading and
 * a table with no rows in it were the same picture. `Loader` is the mark with a
 * light running through it, in whichever of the three colours the surface is
 * painted in.
 *
 * `label` names which request, for the screens that have more than one out at
 * once. It is announced rather than decorative — see `Loader`.
 */
export function Loading({ label }: { label?: string }): React.ReactElement {
  return <Loader block {...(label ? { label } : {})} />;
}

function Refusal({ error }: { error: ApiError }): React.ReactElement {
  // 401 means every credential this client holds was refused after the gate had
  // already let the page through — the gate reads the session, requests prefer
  // the launcher's device token. Sign in rather than reload: a reload arrives
  // back in the same state, which is the loop this replaced.
  if (error.status === 401) {
    if (typeof window !== 'undefined') {
      window.location.href = `/sign-in?redirect=${encodeURIComponent(window.location.pathname)}`;
    }
    return <Loading label="Taking you to sign in" />;
  }

  return <Empty>{error.message}</Empty>;
}

/*
  `RegistrationClosed` and `RegistrationClosedLine` were here, and went with the
  studios (#144).

  They were what stood where the studio registration form used to: a panel on
  the screens where the closure was the whole page, and one sentence on the
  screens that had already said something else. #125 shut the form and #142
  removed the address it had offered, on the argument that a closure with
  nowhere to go is still better than a page that says nothing about the link
  somebody followed to reach it.

  That argument only holds while there is a shut door to describe. The form is
  gone, the route that rendered it is gone, and every screen that showed one of
  these has gone with them — there is nothing left to tell anybody is closed.
*/

/**
 * A table, in the one style this app uses.
 *
 * **Keyed by position, which is the one place in this app that is right.** A
 * heading is a label and several of these tables have more than one column that
 * needs no label — the actions column at the end, and the two-column fact lists
 * on the account and payout screens, which are `['', '']`. Keying on the text
 * made those two `key=""` and React refuses a duplicate key, which is where the
 * *"Encountered two children with the same key, ``"* in the console came from.
 *
 * The usual objection to an index key does not apply: this row is a fixed list
 * declared at the call site, nothing is inserted into it, and it is never
 * reordered — so the index *is* the identity.
 */
export function Table({
  head,
  children,
}: {
  head: readonly string[];
  children: ReactNode;
}): React.ReactElement {
  return (
    <table className={ui.table ?? ''}>
      <thead>
        <tr>
          {head.map((column, position) => (
            // eslint-disable-next-line react/no-array-index-key
            <th key={position}>{column}</th>
          ))}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  );
}

/**
 * A page that asks for exactly one thing, with no navigation.
 *
 * Sign in, sign up, request a reset, set a new password. All four rendered as
 * `<main className="auth">` — a class styled nowhere — around bare
 * `<label>Email <input></label>` pairs, so the first screen anybody with an
 * account ever saw was platform-default inputs on the default background, with
 * nothing on it saying whose sign-in it was.
 *
 * They deliberately do not use `Screen`, because `Screen` renders `Page` and
 * `Page` renders the navigation — every entry of which is behind the session
 * being established here. What they take instead is the wordmark and one panel,
 * which is the waitlist's shape for a page with a single ask.
 *
 * `data-gb-space` is what resolves `--gb-accent`; without it these pages fall
 * back to the `:root` default, which is the developers' blue on whichever
 * audience is actually reading.
 */
export function AuthScreen({
  title,
  children,
  foot,
}: {
  title: string;
  children: ReactNode;
  /** Links out: recovery, the other mode. Set off by a rule. */
  foot?: ReactNode;
}): React.ReactElement {
  const site = currentSite();

  return (
    <main className={ui.authShell ?? ''} data-gb-space={site}>
      <div className={ui.authPanel ?? ''}>
        <div className={ui.authHead ?? ''}>
          {/*
            Whose page this is. Somebody arriving from an email link has no
            other way to tell before they type a password.
          */}
          <Logo site={site} size="1.5rem" />
          <h1 className="gb-headline">{title}</h1>
        </div>

        <div className={ui.panel ?? ''}>
          <div className={ui.stack ?? ''}>
            {children}
            {foot ? <div className={ui.authFoot ?? ''}>{foot}</div> : null}
          </div>
        </div>
      </div>
    </main>
  );
}

/**
 * Where to go after signing in, when the caller asked for somewhere.
 *
 * **Only a path on this origin.** `?redirect=` is attacker-controlled — it is
 * in a URL, and the store links people here through it on every purchase — so
 * an unchecked value makes this an open redirect on the one page where a
 * convincing address bar is worth the most: send somebody to
 * `/sign-in?redirect=https://goodbulld.cc/...` and they sign in on the real
 * site, then land on a copy, having watched the domain they trust hand them
 * over.
 *
 * Anything not starting with a single `/` is refused, which covers absolute
 * URLs, scheme-relative `//host` and `\host` — the last two being the forms
 * that look relative and are not.
 *
 * Here rather than in `sign-in.tsx` because signing up reads it too: somebody
 * who pressed *Buy* while signed out and made an account instead of
 * signing in came for the same reason and should land in the same place. One
 * copy, because the check is the whole value and a second copy is a second
 * chance to write it slightly wrong.
 */
export function returnTo(): string | null {
  const value = new URLSearchParams(window.location.search).get('redirect');
  if (value === null) return null;
  if (!value.startsWith('/') || value.startsWith('//') || value.startsWith('/\\')) return null;
  return value;
}

/**
 * A labelled control.
 *
 * The label is a `<span>` inside the `<label>` rather than a sibling, so the
 * association needs no `htmlFor`/`id` pair to maintain — and a field that loses
 * that pairing is one a screen reader announces as unlabelled, which is
 * invisible to everyone who does not use one.
 */
export function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}): React.ReactElement {
  return (
    <label className={ui.field ?? ''}>
      <span className={ui.fieldLabel ?? ''}>{label}</span>
      {children}
    </label>
  );
}

/**
 * The countries a flow offers, as a filtered list reads them.
 *
 * One list for the form that asks — applying as a creator — because it is
 * checked against `isSupportedCountry` on the server, and a picker that offered
 * a country the endpoint refuses would be a question with a wrong answer in it.
 * `COUNTRIES`
 * is already sorted by name, which is the order the picker shows them in.
 *
 * The code is the value because it is what the column stores; `ChoiceField`
 * prints it beside the name, so `PL` is visible to somebody who knows the code
 * and irrelevant to somebody who does not.
 */
export const COUNTRY_OPTIONS = COUNTRIES.map((country) => ({
  value: country.code,
  label: country.name,
}));

/**
 * How a flow's `check` step asks whether a public address is free.
 *
 * **Every `<Flow>` in this app has to be handed one of these, and the reason is
 * that forgetting fails silently.** The engine's own default fetches
 * `/api/availability/:kind` on the page's own origin, which is where the
 * endpoint lived when one Next deployment served the site and the API together.
 * It does not live there now: the React app is on `app.goodbuild.cc` and the
 * endpoint is `GET /v1/availability/:kind` behind a bearer token. So the
 * default request 404s, and `runCheck` reads *any* failure as no objection —
 * which is the correct policy for a network blip and a catastrophe for a
 * misconfiguration, because the step keeps rendering, keeps letting people
 * past, and never checks anything. Three of this app's four onboarding flows
 * shipped in exactly that state (#87).
 *
 * A failed request still resolves to "no objection", deliberately. The write
 * that creates the row checks the same thing inside the same statement, so this
 * is a courtesy that makes the common collision — the obvious name is gone —
 * discoverable at the moment it is cheap to fix. Treating a blip as "taken"
 * would strand somebody on a name that is actually free.
 *
 * **A `developer-handle` branch was here and went with the studios** (#144). A
 * developer handle and a creator handle were separate namespaces, so routing it
 * to `/availability/creator-handle` would have told somebody their own creator
 * handle was taken by them — it asked `GET /v1/developer-profiles/available`
 * instead. There are no developer profiles and no such endpoint.
 *
 * **`submit.tsx` used to build the same resolver inline, and does not now**
 * (#127). The reason it did was real — it drives an address walk that tries
 * `tiny-city`, `tiny-city-2`, `tiny-city-3` until the catalogue says yes, so it
 * needs the raw lookup as well as the `check` prop — and it stopped being a
 * reason when that walk moved to `useStoreAddress` in `./title-form` and took
 * the resolver as an argument. Every flow left asks through this one function,
 * which is what makes the paragraph above about silent no-objection failures
 * worth writing once.
 */
export function useAvailability(): (kind: string, value: string) => Promise<string | null> {
  const call = useApiMutation();

  return useMemo(
    () => async (kind: string, value: string) => {
      const result = await call<{ taken: boolean; reason?: string }>(
        `/availability/${kind}?value=${encodeURIComponent(value)}`,
        { method: 'GET' },
      );
      if (!result.taken) return null;
      return result.reason ?? 'That address is taken. Try another.';
    },
    [call],
  );
}

/*
  `useVerifier` was here, and went with the studios (#144).

  It was how a flow's `verify` step (#102) asked a country's company register
  whether a number belonged to a company, through
  `GET /v1/organisations/company-number` — the endpoint that actually made the
  call, because no register on earth sends CORS headers at a browser. The one
  flow that asked was the studio registration, for a registered business naming
  its register entry.

  `useAvailability` above is unaffected. It asks our own database whether an
  address is free, which is a different question and still has a caller.
*/

/** Something the reader has to act on. Accent rule, never a colour of its own. */
export function Notice({
  children,
  alert,
}: {
  children: ReactNode;
  alert?: boolean;
}): React.ReactElement {
  return (
    <p className={`gb-body ${ui.notice ?? ''}`} {...(alert ? { role: 'alert' } : {})}>
      {children}
    </p>
  );
}

/**
 * A flex column with a gap, as an inline style.
 *
 * Every public page ported from the storefront brought one of these in a scoped
 * `<style>` block — `.header`, `.titles`, `.block`, a bare `column` const —
 * because an `.astro` file can declare CSS beside its markup and a route in
 * this app cannot. `ui.stack` is the one in the stylesheet and it is fixed at
 * `--gb-space-4`; these pages want two, three, four and six, and a component
 * for each would be four names for one rule.
 *
 * The step rather than a length, so a caller cannot pass `12px` and put a
 * measurement outside the scale on the page.
 */
export function column(step: 2 | 3 | 4 | 6): React.CSSProperties {
  return { display: 'flex', flexDirection: 'column', gap: `var(--gb-space-${step})` };
}

/**
 * Not found.
 *
 * **One page for two situations that used to have two.** The storefront had
 * `404.astro` for a public URL that does not exist and this app had `NotHere`
 * for a path its router does not serve, and the second spent most of its length
 * deciding whether to hand the URL to the other origin. There is one origin, so
 * there is one answer, and it is this.
 *
 * The way out is `/browse`. It is fixed rather than chosen: browse answers
 * honestly on an empty catalogue, so there is no state in which sending
 * somebody there is a door to an empty room with nothing said about it.
 *
 * `site` is passed rather than read from the host, because a not-found is
 * reached from anywhere and the store's chrome is the one with a way back into
 * the product for a stranger.
 */
export function NotFound(): React.ReactElement {
  useDocumentTitle('Not found');

  return (
    <Page site="store">
      <div style={column(4)}>
        <p className="gb-eyebrow">404</p>
        <h1 className="gb-headline">Not found</h1>
        <p className="gb-body">That page is not here. It moved, or the link was wrong.</p>
      </div>

      <div style={{ display: 'flex', gap: 'var(--gb-space-3)', flexWrap: 'wrap' }}>
        <Cta href="/">Our games</Cta>
      </div>
    </Page>
  );
}
